import { getTestOverrides } from '@wix/cli-test-overrides';
import pRetry from 'p-retry';
import type { Options } from 'p-retry';
import { variant, fields } from 'variant';
import { isHttpError, isHttpApplicationError } from '@wix/cli-http-client';
import type { HttpClient } from '@wix/cli-http-client';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { getAppByVersion } from '@wix/ambassador-devcenter-apps-v1-app-old/http';
import {
  getApp,
  createApp,
  setNamespace,
  setCodeIdentifier,
  queryApp,
} from '@wix/ambassador-devcenter-apps-v1-app/http';

import {
  createOAuthApp,
  updateOAuthApp,
} from '@wix/ambassador-headless-v1-o-auth-app/http';
import { queryMyApps } from '@wix/ambassador-devcenter-myapps-v1-my-app/http';
import { SortOrder } from '@wix/ambassador-devcenter-myapps-v1-my-app/types';
import {
  getDevelopmentSites,
  createDevelopmentSite,
  getDevelopmentSitesLimit,
} from '@wix/ambassador-devcenter-dsm-v1-development-site/http';
import {
  queryMarketListing,
  updateMarketListing,
} from '@wix/ambassador-devcenter-app-market-listing-v1-market-listing/http';
import {
  listAppTemplates,
  createAppFromTemplate,
  createBlocksAppFromTemplate,
} from '@wix/ambassador-devcenter-apps-v1-app-template/http';
import { managedApps } from '@wix/ambassador-devcenter-managedapps-v1-managed-app/http';
import { getOrCreateCompanionApp } from '@wix/ambassador-devcenter-apps-companionapps-v1-companion-app/http';
import {
  getInstalledApps,
  installApp,
} from '@wix/ambassador-apps-v1-app-instance/http';
import { V1TenantType } from '@wix/ambassador-apps-v1-app-instance/types';
import {
  AppGroup,
  IncompleteSetupReason,
} from '@wix/ambassador-devcenter-managedapps-v1-managed-app/types';
import { createAppPermission } from '@wix/ambassador-devcenter-apps-v1-app-permission/http';
import type { OAuthApp } from '@wix/ambassador-headless-v1-o-auth-app/types';
import {
  listShareUrls,
  createShareUrl,
} from '@wix/ambassador-devcenter-v1-share-url/http';
import { VersionType } from '@wix/ambassador-devcenter-v1-share-url/types';
import type { ShareUrl } from '@wix/ambassador-devcenter-v1-share-url/types';
import type { CliAppTemplate } from './schemas.js';
import {
  DevelopmentTool,
  cliAppTemplateSchema,
  queryAppTemplateSchema,
  createAppSchema,
  createDevelopmentSiteSchema,
  getDevelopmentSitesLimitSchema,
  getDevelopmentSitesSchema,
  getDeveloperAppsSchema,
  marketListingSchema,
  getAppSchema,
  getAppWithSecretsSchema,
  queryAppSchema,
  managedAppsSchema,
  getInstalledAppsSchema,
  appByVersionSchema,
  createOAuthAppSchema,
  createShareUrlSchema,
  listShareUrlsSchema,
  setCodeIdentifierSchema,
  setNamespaceSchema,
  createCompanionAppSchema,
  installAppSchema,
} from './schemas.js';

const EMPTY_SITE_TEMPLATE = '24bafe8a-7c80-4b63-8a71-08752062f246';

interface CreateAppOptions {
  name: string;
}

interface SetNamespaceOptions {
  appId: string;
  namespace: string;
}

interface SetCodeIdentifierOptions {
  appId: string;
  codeIdentifier: string;
}

interface CreateAppFromTemplateOptions extends CreateAppOptions {
  templateId: string;
}

export const SetCodeIdentifierResult = variant({
  Success: fields<{ codeIdentifier: string }>(),
  ShouldBeUnique: fields<{ codeIdentifier: string }>(),
});

export const SetNamespaceResult = variant({
  Success: fields<{ namespace: string }>(),
  ShouldBeUnique: fields<{ namespace: string }>(),
});

export class DevCenterClient {
  private readonly retryOptions: Options;

  constructor(private readonly httpClient: HttpClient) {
    const { minRetryTimeout } = getTestOverrides();

    this.retryOptions = {
      retries: 3,
      minTimeout: minRetryTimeout,
      maxTimeout: 3 * 1000,
    };
  }

  addPermission = async ({
    appId,
    permissionId,
  }: {
    appId: string;
    permissionId: string;
  }) => {
    try {
      const { data } = await pRetry(
        () =>
          this.httpClient.request(
            createAppPermission({
              appPermission: {
                appId,
                permission: {
                  permissionId,
                },
              },
            })
          ),
        this.retryOptions
      );

      return data;
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToAddPermission(),
        cause: e,
      });
    }
  };

  createApp = async (app: CreateAppOptions) => {
    try {
      const { data } = await pRetry(
        () => this.httpClient.request(createApp({ app })),
        this.retryOptions
      );

      return createAppSchema.parse(data).app;
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToCreateDevCenterApp(),
        cause: e,
      });
    }
  };

  createCompanionApp = async () => {
    try {
      const { data } = await pRetry(
        () => this.httpClient.request(getOrCreateCompanionApp({})),
        this.retryOptions
      );

      return createCompanionAppSchema.parse(data).companionApp;
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToCreateCompanionApp(),
        cause: e,
      });
    }
  };

  setNamespace = async ({ appId, namespace }: SetNamespaceOptions) => {
    try {
      const { data } = await this.httpClient.request(
        setNamespace({ appId, appName: namespace })
      );

      return SetNamespaceResult.Success({
        namespace: setNamespaceSchema.parse(data).namespace,
      });
    } catch (e) {
      if (
        isHttpError(e) &&
        e.response?.status === 409 &&
        isHttpApplicationError(e) &&
        e.response.data.details.applicationError.code === 'ALREADY_EXISTS'
      ) {
        return SetNamespaceResult.ShouldBeUnique({ namespace });
      }

      throw new CliError({
        code: CliErrorCode.FailedToSetNamespace(),
        cause: e,
      });
    }
  };

  setCodeIdentifier = async ({
    appId,
    codeIdentifier,
  }: SetCodeIdentifierOptions) => {
    try {
      const { data } = await this.httpClient.request(
        setCodeIdentifier({ appId, codeIdentifier })
      );

      return SetCodeIdentifierResult.Success({
        codeIdentifier: setCodeIdentifierSchema.parse(data).codeIdentifier,
      });
    } catch (e) {
      if (
        isHttpError(e) &&
        e.response?.status === 409 &&
        isHttpApplicationError(e) &&
        e.response.data.details.applicationError.code === 'ALREADY_EXISTS'
      ) {
        return SetCodeIdentifierResult.ShouldBeUnique({ codeIdentifier });
      }

      throw new CliError({
        code: CliErrorCode.FailedToSetCodeIdentifier(),
        cause: e,
      });
    }
  };

  createOAuthApp = async (oAuthApp: OAuthApp) => {
    try {
      const { data } = await this.httpClient.request(
        createOAuthApp({ oAuthApp })
      );
      return createOAuthAppSchema.parse(data).oAuthApp;
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToCreateDevCenterOAuthApp(),
        cause: e,
      });
    }
  };

  updateOAuthApp = async (oAuthApp: OAuthApp) => {
    try {
      const { data } = await this.httpClient.request(
        updateOAuthApp({
          oAuthApp,
          mask: [
            'allowedDomains',
            'allowedRedirectUris',
            'redirectUrlWixPages',
            'origin',
          ],
        })
      );
      return createOAuthAppSchema.parse(data).oAuthApp;
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToUpdateDevCenterOAuthApp(),
        cause: e,
      });
    }
  };

  getAppSecret = async ({ appId }: { appId: string }) => {
    try {
      const { data } = await pRetry(
        () => this.httpClient.request(getApp({ appId, withSecrets: true })),
        this.retryOptions
      );

      return getAppWithSecretsSchema.parse(data).app.appSecrets;
    } catch (e) {
      if (
        isHttpError(e) &&
        e.response?.status === 403 &&
        isHttpApplicationError(e) &&
        e.response.data.details.applicationError.code === 'PERMISSION_DENIED'
      ) {
        throw new CliError({
          code: CliErrorCode.DeniedPermissionToGetAppSecret(),
          cause: e,
        });
      }
      throw new CliError({
        code: CliErrorCode.FailedToGetAppSecret(),
        cause: e,
      });
    }
  };

  getApp = async ({ appId }: { appId: string }) => {
    try {
      const { data } = await pRetry(
        () => this.httpClient.request(getApp({ appId })),
        this.retryOptions
      );

      return getAppSchema.parse(data).app;
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToGetApp(),
        cause: e,
      });
    }
  };

  /**
   * This method is deprecated.
   */
  getAppByVersion = async ({
    appId,
    version = 'latest',
  }: {
    appId: string;
    version?: string;
  }) => {
    try {
      const { data } = await pRetry(
        () => this.httpClient.request(getAppByVersion({ appId, version })),
        this.retryOptions
      );

      return appByVersionSchema.parse(data);
    } catch (cause) {
      if (isHttpError(cause) && cause.response?.status === 404) {
        throw new CliError({
          code: CliErrorCode.AppForVersionNotFound({
            version,
          }),
          cause,
        });
      }

      throw new CliError({
        code: CliErrorCode.FailedToFetchAppForVersion(),
        cause,
      });
    }
  };

  createDevSite = async (
    { templateId }: { templateId: string } = { templateId: EMPTY_SITE_TEMPLATE }
  ) => {
    try {
      const { data } = await pRetry(
        () =>
          this.httpClient.request(
            createDevelopmentSite({
              templateId,
            })
          ),
        this.retryOptions
      );

      return createDevelopmentSiteSchema.parse(data).developmentSite;
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToCreateDevelopmentSite(),
        cause: e,
      });
    }
  };

  getDevSites = async () => {
    try {
      const { data } = await pRetry(
        () => this.httpClient.request(getDevelopmentSites({})),
        this.retryOptions
      );

      return getDevelopmentSitesSchema.parse(data).developmentSites;
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToGetDevelopmentSites(),
        cause: e,
      });
    }
  };

  getDevSitesCount = async () => {
    const developmentSites = await this.getDevSites();

    return developmentSites.length;
  };

  getDevSite = async (id: string) => {
    const developmentSites = await this.getDevSites();

    return developmentSites.find((site) => site.id === id) ?? null;
  };

  getDeveloperApps = async () => {
    try {
      const { data } = await pRetry(
        () =>
          this.httpClient.request(
            queryMyApps({
              query: {
                paging: {
                  limit: 500,
                },
                sort: [
                  {
                    fieldName: 'updatedDate',
                    order: SortOrder.DESC,
                  },
                ],
              },
            })
          ),
        this.retryOptions
      );

      return getDeveloperAppsSchema.parse(data).myApps;
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToGetDeveloperApps(),
        cause: e,
      });
    }
  };

  getDevSitesLimit = async () => {
    try {
      const { data } = await pRetry(
        () => this.httpClient.request(getDevelopmentSitesLimit({})),
        this.retryOptions
      );

      return getDevelopmentSitesLimitSchema.parse(data).limit;
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToGetDevelopmentSitesLimit(),
        cause: e,
      });
    }
  };

  private getMarketListing = async (appId: string) => {
    try {
      const { data } = await pRetry(
        () =>
          this.httpClient.request(
            queryMarketListing({
              query: {
                filter: {
                  appId,
                  languageCode: 'en',
                },
              },
            })
          ),
        this.retryOptions
      );

      return data.marketListing?.[0]
        ? marketListingSchema.parse(data.marketListing[0])
        : undefined;
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToGetMarketListing(),
        cause: e,
      });
    }
  };

  getRequiredAppIds = async (appId: string) => {
    try {
      const marketListing = await this.getMarketListing(appId);
      return (
        marketListing?.installationRequirement?.requiredApps.map(
          ({ id }) => id
        ) ?? []
      );
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToGetRequiredApps(),
        cause: e,
      });
    }
  };

  getRequiredApps = async (appId: string) => {
    const requiredAppIds = await this.getRequiredAppIds(appId);

    if (requiredAppIds.length === 0) {
      return [];
    }

    try {
      const { data } = await pRetry(
        () =>
          this.httpClient.request(
            queryApp({
              query: {
                filter: {
                  id: {
                    $in: requiredAppIds,
                  },
                },
              },
            })
          ),
        this.retryOptions
      );

      return queryAppSchema.parse(data).apps;
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToQueryApps(),
        cause: e,
      });
    }
  };

  addRequiredApp = async ({
    appId,
    appIdToRequire,
  }: {
    appId: string;
    appIdToRequire: string;
  }) => {
    try {
      const marketListing = await this.getMarketListing(appId);

      const appAlreadyRequired =
        marketListing?.installationRequirement?.requiredApps.some(
          (requiredApp) => requiredApp.id === appIdToRequire
        );

      if (marketListing && !appAlreadyRequired) {
        const newRequiredApps = [
          ...(marketListing.installationRequirement?.requiredApps ?? []),
          {
            id: appIdToRequire,
            slug: appIdToRequire,
          },
        ];

        await pRetry(
          () =>
            this.httpClient.request(
              updateMarketListing({
                marketListing: {
                  ...marketListing,
                  appId,
                  installationRequirement: {
                    requiredApps: newRequiredApps,
                  },
                },
                mask: ['installationRequirement.requiredApps'],
              })
            ),
          this.retryOptions
        );
      }
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToAddRequiredApp(),
        cause: e,
      });
    }
  };

  queryCliAppTemplates = async () => {
    try {
      const { data } = await pRetry(
        () => this.httpClient.request(listAppTemplates({})),
        this.retryOptions
      );

      const { appTemplates } = queryAppTemplateSchema.parse(data);

      return appTemplates
        .filter((template) =>
          template.developmentTools.includes(DevelopmentTool.CLI)
        )
        .map((template) => cliAppTemplateSchema.parse(template));
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToQueryCliAppTemplates(),
        cause: e,
      });
    }
  };

  getCliAppTemplate = async (id: string) => {
    const cliAppTemplates = await this.queryCliAppTemplates();

    return cliAppTemplates.find((template) => template.id === id) ?? null;
  };

  createAppFromTemplate = async (
    { templateId, name }: CreateAppFromTemplateOptions,
    template: CliAppTemplate
  ) => {
    try {
      const { data } = await pRetry(() => {
        const createFromTemplate = template.developmentTools.includes(
          DevelopmentTool.BLOCKS
        )
          ? createBlocksAppFromTemplate
          : createAppFromTemplate;

        return this.httpClient.request(
          createFromTemplate({
            templateId,
            templateConfig: { appInfo: { name } },
          })
        );
      }, this.retryOptions);

      return createAppSchema.parse(data).app;
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToCreateDevCenterAppFromTemplate(),
        cause: e,
        info: { templateId },
      });
    }
  };

  private getAppInstallationDetails = async (appId: string) => {
    try {
      const { data } = await pRetry(
        () => this.httpClient.request(managedApps({})),
        this.retryOptions
      );

      return managedAppsSchema
        .parse(data)
        .managedApps.find((app) => app.appId === appId)?.installationDetails;
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToGetAppInstallationDetails({ appId }),
        cause: e,
      });
    }
  };

  installApp = async (siteId: string, appId: string) => {
    try {
      const { data } = await pRetry(
        () =>
          this.httpClient.request(
            installApp({
              tenant: {
                id: siteId,
                tenantType: V1TenantType.SITE,
              },
              appInstance: {
                appDefId: appId,
                version: 'latest',
              },
            })
          ),
        this.retryOptions
      );

      return installAppSchema.parse(data);
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToInstallApp({ appId, siteId }),
        cause: e,
      });
    }
  };

  isAppInstalled = async (appId: string) => {
    try {
      // `getInstalledApps` allows to get also Wix apps in the list of installed apps.
      // In other words this API will work for both external and internal developers.
      const { data } = await pRetry(
        () => this.httpClient.request(getInstalledApps({})),
        this.retryOptions
      );

      return getInstalledAppsSchema
        .parse(data)
        .appInstances.some((app) => app.appDefId === appId);
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToGetAppInstallationDetails({ appId }),
        cause: e,
      });
    }
  };

  isAppInstalledInEditor = async (appId: string) => {
    const installationDetails = await this.getAppInstallationDetails(appId);

    return (
      installationDetails?.group === AppGroup.INSTALLED &&
      installationDetails.incompleteSetupReason !==
        IncompleteSetupReason.PENDING_EDITOR
    );
  };

  private createShareUrl = async (shareUrl: ShareUrl) => {
    try {
      const { data } = await this.httpClient.request(
        createShareUrl({
          shareUrl,
          disableExistingWithSameType: true,
          shareUrlIdParameter: ':shareUrlId',
          urlTemplate: 'https://wix.com/app-market/test/:shareUrlId',
        })
      );

      return createShareUrlSchema.parse(data).shareUrl;
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToCreateShareUrl(),
        cause: e,
      });
    }
  };

  private listShareUrls = async (appId: string) => {
    try {
      const { data } = await pRetry(
        () => this.httpClient.request(listShareUrls({ appId })),
        this.retryOptions
      );

      return listShareUrlsSchema.parse(data).shareUrls;
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToListShareUrls(),
        cause: e,
      });
    }
  };

  resolveDevVersionShareUrl = async (appId: string) => {
    const shareUrls = await this.listShareUrls(appId);

    const devVersionShareUrl = shareUrls.find(
      (shareUrl) => shareUrl.versionType === VersionType.DEV_VERSION
    );

    if (devVersionShareUrl) {
      return devVersionShareUrl;
    }

    return this.createShareUrl({ appId, versionType: VersionType.DEV_VERSION });
  };
}
